import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, treatsSeveral } from '../skillUsage';

export const cardGridAssembleSkill: CodeoSkill = {
    name: SKILL_NAMES.cardGridAssemble,
    kind: 'codeo',
    description:
        'Assemble a set of capability or feature cards into a grid, each sliding a short distance ' +
        'into its own slot — for a feature tour, a set of integrations, or anything where the ' +
        'shape of the whole set is the point.',
    checkLooksFor: 'a grid container, and one treatment reaching several cards',
    documentUsesSkill: check(
        (script, markup) => /grid-template-columns/.test(markup) && treatsSeveral(script),
    ),
    instructions: `# Card Grid Assemble

Four to nine small cards land into a grid, one after another, each sliding a short distance *into its own slot* rather than flying in from off-frame. When the last one lands the layout is complete and the frame goes still. The reading is "here is the whole set" — the assembly is what tells you the set is finite.

This is the layout to reach for when the brief names a collection. It is also the layout the style guidance warns about, so read the constraint below before using it.

**One deliberate hierarchy break.** An even grid of identical cards is the single biggest "generated" tell. Make one card larger, or heavier, or the only one carrying the accent color — the one that is actually the point. If no card deserves to be the point, the content is a list, and \`${SKILL_NAMES.staggeredListReveal}\` will serve it better.

## Markup

Lay the grid out with CSS grid inside an absolutely positioned stage. Span the lead card across two columns so the break is structural rather than decorative. **Name the set above it** — a grid on its own is five nouns with nothing saying what they are collectively.

\`\`\`html
<div id="grid-heading" style="position: absolute; left: 200px; top: 130px; font-family: var(--l5-font-0), sans-serif; font-size: 62px; font-weight: 800; letter-spacing: -0.02em; color: var(--l5-color-0, #ffffff);">Everything it does</div>
<div id="grid" style="position: absolute; left: 200px; top: 260px; width: 1520px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px;">
  <div class="card" style="grid-column: span 2; padding: 40px; border-radius: 20px; background: var(--l5-color-1, #101820); border: 1px solid rgba(255,255,255,0.12);">
    <div style="font-family: var(--l5-font-0), sans-serif; font-size: 48px; font-weight: 800; color: var(--l5-color-2, #38bdf8);">Permissions</div>
    <div style="margin-top: 10px; font-family: var(--l5-font-0), sans-serif; font-size: 26px; font-weight: 500; color: rgba(255,255,255,0.66);">Scoped per workspace</div>
  </div>
  <div class="card" style="padding: 40px; border-radius: 20px; background: var(--l5-color-1, #101820); border: 1px solid rgba(255,255,255,0.12);">
    <div style="font-family: var(--l5-font-0), sans-serif; font-size: 34px; font-weight: 700; color: var(--l5-color-0, #ffffff);">Audit trail</div>
  </div>
  <!-- remaining cards -->
</div>
\`\`\`

## Timeline code

Each card slides a *short* distance — 24px, not 300px — so it reads as settling into a slot that was already there.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const SETTLE = 0.62;

gsap.set('#grid-heading', { y: 18, opacity: 0 });
gsap.set('.card', { y: 24, opacity: 0 });

tl.to('#grid-heading', { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' }, 0);

tl.to('.card', {
    y: 0,
    opacity: 1,
    duration: SETTLE,
    ease: 'power3.out',
    stagger: 0.09,
}, 0.2);

window.__timelines = { main: tl };
\`\`\`

\`power3.out\` gives a long decelerating tail, so cards arrive quickly and settle slowly. That settle is what reads as weight.

## Adapt

- **Stagger order**: \`stagger: 0.09\` runs in DOM order. \`stagger: { each: 0.09, from: 'start' }\` is explicit; \`from: 'center'\` radiates outward, which suits an odd-numbered grid with a hero in the middle.
- **Vertical list**: one column with wider cards. Same tween, and the stagger reads as a build rather than an assembly.
- **Slide direction**: \`x: -24\` instead of \`y: 24\` when the grid sits beside a headline and should feel like it is arriving from it.
- **Icons**: a thin-line \`<svg>\` per card, drawn on as the card lands — see \`${SKILL_NAMES.svgDrawOn}\` for the dash technique, offset to start when that card's slide begins.
- **A second phase**: once assembled, the lead card can grow and the rest recede (\`opacity: 0.4\`) to hand off to whatever comes next. That is a phase change, not a second idea — see \`${SKILL_NAMES.elementMorphHandoff}\`.

## Pitfalls

- **Every selector in the timeline has to exist in the markup.** A tween on an id or class you never wrote is silent — no error, no lint finding, just a piece of the composition that never moves. Read the two side by side before you finish; an ambient layer that quietly does nothing is the most common way this graphic ends up static.
- Don't leave the grid unlabelled. Five cards with no heading is a set with no claim attached to it.
- Cards must not scatter in from off-frame or overshoot. A short slide into an existing slot reads as designed; a fly-in with \`back.out\` reads as a template.
- Never let the grid finish assembling and then just sit there for the rest of the scene. Either give it a second phase or let an ambient layer carry the movement — see \`${SKILL_NAMES.ambientFloatLoop}\`.
- Six is about the ceiling for cards a viewer can take in. Past that the grid becomes texture and the labels stop being read; if the count itself is the point, use \`${SKILL_NAMES.dataAsMetaphor}\` instead.
- The cards are \`y\`-tweened, so no card may carry a CSS \`transform\` — size the hierarchy break with \`grid-column\` and font size, not \`scale\`.
- \`gsap.set\` hides the cards at build time, so the stagger must not add \`immediateRender: false\`.
- Don't put a body line on every card. One line of support on the lead card and bare labels elsewhere keeps the hierarchy readable.
- Equal-size cards with a left-edge accent stripe each is slide-deck furniture. Vary the cards instead.`,
};
